import React, { PureComponent, ReactNode } from 'react';
import { Button, notification, Space } from 'antd';
import { Flex } from '../../common/Flex';
import { TvDetails } from './details/TvDetails';
import { ModalPanelProps } from '../DashboardModal';
import TimeUtils from '../../../common/util/TimeUtils';
import {
    CapacityValue,
    CountType,
    FlightDto,
    FlightId,
    MeasureType,
    Otmv,
    RegulationDto,
    TimePeriod,
    TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
    TrafficType,
} from 'app/model/CORE';
import { FlightListPanel } from '../flights/FlightListPanel';
import { RegulationPeriods } from 'app/model/client/CORE';
import TimePeriodUtils from '../../../common/util/TimePeriodUtils';
import './TvDetailsPanel.less';
import { ModalHandler } from '../ModalHandler';
import ExtensiblePanel, { ExtensiblePanelProps, ExtensiblePanelState } from '../../../common/panel/ExtensiblePanel';
import Utils from '../../../common/util/Utils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { MeteoForecast, SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE } from 'app/model/SOL49';
import MeasureUtils from 'app/common/util/MeasureUtils';
import AppContext from '../../../common/context/AppContext';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';

const mapStateToProps = (state: RootState) => {
    return {
        currentDataVersion: state.dashboardData.currentDataVersion,
    };
};
const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface TvDetailsPanelProps extends ModalPanelProps, ExtensiblePanelProps, PropsFromRedux {
    currentTime: Date;
    tvId: string;
    tvSet: string;
    regulationPeriods?: Array<RegulationPeriods>;
    occupancyPeriods?: Map<FlightId, TimePeriod>;
    predictedCapacity?: CapacityValue[];
}

interface TvDetailsPanelState extends ExtensiblePanelState {
    loading: boolean;
    monitoredPeriod: TimePeriod;
    selectedPeriod: TimePeriod;
    selectedCountingPeriod: TimePeriod;
    selectedPeriodType: CountType;
    trafficType: TrafficType;
    hourlyEntryCapacity: Array<CapacityValue>;
    lastUpdate: Date;
    otmvs: Array<Otmv>;
    countType: CountType;
}

export class TvDetailsPanel extends ExtensiblePanel<TvDetailsPanelProps, TvDetailsPanelState> {
    static MAIN_PANEL_WIDTH_EM = 60;
    static SIDE_PANEL_WIDTH_EM = 82;
    public static INITIAL_WIDTH = TvDetailsPanel.MAIN_PANEL_WIDTH_EM + 'em';

    private flightListPanelRef = React.createRef<FlightListPanel>();

    constructor(props) {
        super(props);
        this.state = Object.assign({}, this.state, {
            monitoredPeriod: AppContext.getMonitoringPeriod(),
            selectedPeriod: undefined,
            selectedCountingPeriod: undefined,
            selectedPeriodType: undefined,
            trafficType: TrafficType.LOAD,
            lastUpdate: AppContext.getCurrentTime(),
            countType: CountType.ENTRY,
        });
    }

    handleSelectedPeriodChange = (
        countType: CountType,
        selectedPeriod: TimePeriod,
        trafficWindow: TimePeriod,
        hourlyEntryCapacity?: Array<CapacityValue>,
        otmvs?: Otmv[],
    ) => {
        const sidePanelVisible = this.state.panelIdxVisible > 0;

        this.setState(
            {
                selectedPeriod: selectedPeriod,
                selectedCountingPeriod: trafficWindow,
                selectedPeriodType: countType,
                hourlyEntryCapacity: hourlyEntryCapacity,
                otmvs: otmvs,
            },
            () => !sidePanelVisible && this.showSidePanel(),
        );
    };

    handleTrafficTypeChange = (trafficType: TrafficType) => {
        this.setState({ trafficType: trafficType });
    };
    handleManualRefresh = () => {
        this.flightListPanelRef.current?.refreshFlightList();
    };

    getPanels() {
        const panels = new Map<ReactNode, number>();
        panels.set(this.getMainPanelContent(), TvDetailsPanel.MAIN_PANEL_WIDTH_EM);
        panels.set(this.getSidePanelContent(), TvDetailsPanel.SIDE_PANEL_WIDTH_EM);
        return panels;
    }

    getMainPanelContent = () => {
        return (
            <Flex>
                <Flex className="tv-details-panel" direction="vertical">
                    <Flex className="modal-title" size="2em">
                        <Flex size="15em" className={'update-time'}>
                            last update{' '}
                            {TimeUtils.formatDateDDMMYYYY(this.state.lastUpdate, '-') +
                                ' ' +
                                TimeUtils.formatHHMM(this.state.lastUpdate)}
                        </Flex>
                        <Flex direction="vertical">
                            <Flex style={{ fontWeight: 'bold', justifyContent: 'center' }}>
                                TFV - {this.props.tvId}
                            </Flex>
                            <Flex style={{ justifyContent: 'center' }}>{this.props.tvSet}</Flex>
                        </Flex>
                        <Flex size="15em" />
                    </Flex>
                    <TvDetails
                        currentTime={this.props.currentTime}
                        timePeriod={this.state.monitoredPeriod}
                        tvId={this.props.tvId}
                        tvSet={this.props.tvSet}
                        regulationPeriods={this.props.regulationPeriods}
                        occupancyPeriods={this.props.occupancyPeriods}
                        onUpdate={() => this.setState({ lastUpdate: AppContext.getCurrentTime() })}
                        selectedPeriod={this.state.selectedPeriod}
                        selectedPeriodType={this.state.selectedPeriodType}
                        onSelectedPeriodChange={this.handleSelectedPeriodChange}
                        onTrafficTypeChange={this.handleTrafficTypeChange}
                        onManualRefresh={this.handleManualRefresh}
                        onCountTypeChange={countType => this.setState({ countType: countType })}
                        predictedCapacity={this.props.predictedCapacity}
                    />
                    <Flex size="2em" style={{ alignSelf: 'flex-end', paddingTop: '1em' }}>
                        <TvDetailsButtons
                            tvId={this.props.tvId}
                            onClose={this.props.onClose}
                            selectedPeriod={this.state.selectedPeriod}
                            hourlyEntryCapacity={this.state.hourlyEntryCapacity}
                            otmvs={this.state.otmvs}
                            countType={this.state.countType}
                        />
                    </Flex>
                </Flex>
            </Flex>
        );
    };

    getSidePanelContent = () => {
        if (!this.state.selectedPeriod || !this.state.selectedPeriodType || !this.state.selectedCountingPeriod)
            return null;

        // Globally selected flights are highlighted on the graph, and should be "pre" selected in the side panel
        const preSelectedFlightIds =
            this.props.occupancyPeriods &&
            Array.from(this.props.occupancyPeriods)
                .filter(([flightId, tp]) => TimePeriodUtils.intersects(this.state.selectedCountingPeriod, tp))
                .map(([flightId, tp]) => flightId);

        return (
            <Flex style={{ minHeight: '60em' }}>
                <FlightListPanel
                    ref={this.flightListPanelRef}
                    tvId={this.props.tvId}
                    countType={this.state.selectedPeriodType}
                    trafficWindow={this.state.selectedCountingPeriod}
                    trafficType={this.state.trafficType}
                    defaultSelectedFlights={preSelectedFlightIds}
                    onClose={this.props.onClose}
                    extraButtons={this.renderExtraButtons}
                />
            </Flex>
        );
    };

    renderExtraButtons = (selection: Array<FlightDto>): React.ReactNode => {
        const children = [];

        if (!AuthorisationUtils.isFmpMapUser()) {
            children.push(
                <Button
                    key="find-solution"
                    onClick={() =>
                        ModalHandler.showFindSolution([
                            {
                                tvId: this.props.tvId,
                                timePeriod: this.state.selectedPeriod,
                                selected: true,
                            },
                        ])
                    }>
                    Find solution
                </Button>,
            );
        }

        children.push(
            <Button
                disabled={Utils.isNotExists(selection) || Utils.isEmpty(selection)}
                key="show-selected-flights-on-map"
                onClick={() => {
                    this.setState({
                        loading: true,
                    });
                    ServiceFactory.sol49Service()
                        .getMapFlightsData({
                            clazz: SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
                            dataset: selection[0].dataset,
                            flights: selection.map(fDto => fDto.flightId),
                            flightProfileTrafficType: this.state.trafficType,
                            tvIdList: [this.props.tvId],
                            convectionSource: AppContext.getConvectionSource(),
                            monitoringDataVersion: this.props.currentDataVersion,
                        })
                        .then(reply => {
                            if (reply.error) {
                                notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                            } else {
                                // Keep only convections that intersects with TP and FL of selected flights
                                const mapConvection = reply.data?.mapConvection;
                                const forecastPerTvName = reply.data?.mapConvection?.networkForecast?.forecastPerTvName;
                                const polygonMeteoForecasts =
                                    reply.data.mapConvection?.networkForecast.polygonMeteoForecasts;
                                if (Utils.isNotEmpty(polygonMeteoForecasts)) {
                                    mapConvection.networkForecast.polygonMeteoForecasts = polygonMeteoForecasts.filter(
                                        forecast => this.hasImpactOnSelectedFlights(forecast, selection),
                                    );
                                } else if (forecastPerTvName) {
                                    mapConvection.networkForecast.forecastPerTvName = Object.fromEntries(
                                        Object.entries(forecastPerTvName).filter(([_, forecast]) =>
                                            this.hasImpactOnSelectedFlights(forecast, selection),
                                        ),
                                    );
                                }
                                ModalHandler.showMapPanel(reply.data.flights, [this.props.tvId], mapConvection);
                            }
                        })
                        .catch(reason => {
                            notification.error({ message: 'Error', description: reason.message, duration: 0 });
                        })
                        .finally(() =>
                            this.setState({
                                loading: false,
                            }),
                        );
                }}>
                Show on Map
            </Button>,
        );

        return React.createElement(React.Fragment, {}, children);
    };

    private hasImpactOnSelectedFlights(forecast: MeteoForecast, flights: Array<FlightDto>) {
        return flights.some(flight => {
            const entryExitPeriod = {
                from: flight.timeAtReferenceLocationEntry.time,
                to: flight.timeAtReferenceLocationExit.time,
            };
            const entryExitLevel = {
                min: FlightLevelUtils.min(
                    flight.flightLevelAtReferenceLocationEntry,
                    flight.flightLevelAtReferenceLocationExit,
                ),
                max: FlightLevelUtils.max(
                    flight.flightLevelAtReferenceLocationEntry,
                    flight.flightLevelAtReferenceLocationExit,
                ),
            };
            return (
                TimePeriodUtils.intersects(entryExitPeriod, forecast.validityPeriod) &&
                FlightLevelUtils.intersectsExcl(entryExitLevel, forecast.flightLevelBand)
            );
        });
    }
}

interface TvDetailsButtonsProps extends ModalPanelProps {
    tvId: string;
    selectedPeriod?: TimePeriod;
    hourlyEntryCapacity: Array<CapacityValue>;
    otmvs: Array<Otmv>;
    countType: CountType;
}

class TvDetailsButtons extends PureComponent<TvDetailsButtonsProps> {
    handleCloseClick = () => {
        this.props.onClose?.();
    };
    handleNewRegulationClick = () => {
        const initialRegulation: Partial<RegulationDto> = {
            applicability: this.props.selectedPeriod,
            location: {
                id: this.props.tvId,
                clazz: TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
            },
            measureId: {
                id: MeasureUtils.getRegulationId(this.props.tvId),
                type: MeasureType.REGULATION,
            },
            initialConstraints: MeasureUtils.hourlyEntryCapacityToRegInitialConstraints(
                this.props.hourlyEntryCapacity,
                this.props.selectedPeriod,
            ),
            occupancyConstraints: MeasureUtils.otmvsToRegOccupancyConstraints(
                this.props.otmvs,
                this.props.selectedPeriod,
            ),
            calculationType: this.props.countType,
            occupancyDurationInMs: (this.props.otmvs?.[0]?.durationInMin || 1) * TimeUtils.MIN_TO_MS,
        };
        ModalHandler.showRegulationEditor(this.props.tvId, 'tv', initialRegulation);
    };

    handleShowOnMapClick = () => {
        ModalHandler.showMapPanel([], [this.props.tvId]);
    };

    render() {
        return (
            <Space>
                <Button key="close" onClick={this.handleCloseClick}>
                    Close
                </Button>
                <ConditionalRender condition={!AuthorisationUtils.isFmpMapUser()}>
                    <Button key="show-on-map" onClick={this.handleShowOnMapClick}>
                        Show on Map
                    </Button>
                    <Button key="new-scenario" disabled={true}>
                        New scenario
                    </Button>
                    <Button key="new-regulation" onClick={this.handleNewRegulationClick}>
                        New regulation
                    </Button>
                </ConditionalRender>
            </Space>
        );
    }
}

export default connector(TvDetailsPanel);
